import { Filter } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { DateRangePicker, type DateRange } from "@/components/ui/v2/date-range-picker";
import SearchInput from "@/components/ui/v2/search-input";
import { type ActiveFilter, TableActiveFilters } from "@/components/ui/v2/table/table-active-filters";

type HistoryToolbarProps = {
  i18nPrefix: string;
  search: { value: string; onChange: (value: string) => void; placeholder: string };
  totalCount?: number;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  onFiltersClick: () => void;
  activeFilters: ActiveFilter[];
  onRemoveFilter: (filter: ActiveFilter) => void;
  onClearFilters: () => void;
};

export function HistoryToolbar({
  i18nPrefix,
  search,
  totalCount,
  dateRange,
  onDateRangeChange,
  onFiltersClick,
  activeFilters,
  onRemoveFilter,
  onClearFilters,
}: HistoryToolbarProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="w-full md:max-w-md">
          <SearchInput
            placeholder={search.placeholder}
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {totalCount !== undefined && (
            <span className="text-sm text-muted-foreground">{t(`${i18nPrefix}.eventCount`, { count: totalCount })}</span>
          )}
          <DateRangePicker
            initialDateFrom={dateRange.from}
            initialDateTo={dateRange.to}
            maxDate={new Date()}
            onUpdate={({ range }) => onDateRangeChange(range)}
            align="end"
          />
          <Button variant="outline" onClick={onFiltersClick} className="gap-1.5">
            <Filter className="size-4" />
            {t("common.filters")}
            {activeFilters.length > 0 && (
              <Badge variant="info" size="small" className="px-1.5 tabular-nums">
                {activeFilters.length}
              </Badge>
            )}
          </Button>
        </div>
      </div>
      {activeFilters.length > 0 && (
        <TableActiveFilters filters={activeFilters} onRemoveFilter={onRemoveFilter} onClearFilters={onClearFilters} />
      )}
    </div>
  );
}
